import type { CandidateRecord } from "@/types";

const ELEMENT_NAME_OVERRIDES: Record<string, string> = {
  scr_1: "Screenshot 1",
  scr_2: "Screenshot 2",
  scr_3: "Screenshot 3",
  scr_4: "Screenshot 4",
  scr_5: "Screenshot 5",
  scr_6: "Screenshot 6",
  scr_7: "Screenshot 7",
  scr_8: "Screenshot 8",
  scr_9: "Screenshot 9",
  scr_10: "Screenshot 10",
  feature_graphic: "Feature graphic",
  long_desc: "Long description",
  short_desc: "Short description",
  promo_text: "Promo text",
  icon: "App icon",
  subtitle: "Subtitle",
  title: "App title",
};

/**
 * Parse an `element_id` (locale-scoped synthesized key per
 * `docs/product/hypothesis-and-signal.md` Locale Cluster Model) into a
 * readable name + locale pair. Examples:
 *   "scr_1:en-US"       -> { name: "Screenshot 1", locale: "en-US" }
 *   "subtitle:de-DE"    -> { name: "Subtitle", locale: "de-DE" }
 *   "icon"              -> { name: "App icon", locale: null }
 *   "custom_xyz:en-GB"  -> { name: "Custom xyz", locale: "en-GB" }
 */
export function parseElementId(elementId: string): {
  name: string;
  locale: string | null;
} {
  const [rawName, locale = null] = elementId.split(":");
  const overridden = ELEMENT_NAME_OVERRIDES[rawName];
  if (overridden) {
    return { name: overridden, locale };
  }
  // Fallback: title-case the raw name.
  const name = rawName
    .split(/[_-]/)
    .map((word, i) =>
      i === 0
        ? word.charAt(0).toUpperCase() + word.slice(1)
        : word.toLowerCase(),
    )
    .join(" ");
  return { name, locale };
}

const ELEMENT_TYPE_LABEL: Record<CandidateRecord["element_type"], string> = {
  visual_icon: "Icon",
  visual_screenshot: "Screenshot",
  visual_video: "Feature graphic",
  text_title: "Title",
  text_subtitle: "Subtitle",
  // Paid-routing tests change which product page paid traffic lands on;
  // they test no creative surface, so they read as routing, not as an
  // element. The `?? humanizeToken(type)` fallback below would render
  // "Asa paid cpp routing" — correct but not English.
  asa_paid_cpp_routing: "Apple Search Ads routing",
  google_ads_paid_cpp_routing: "Google Ads routing",
};

/**
 * Humanize an unknown enum token as a drift fallback:
 * "visual_thing" -> "Visual thing". Used when a backend enum value
 * (cast from network data) is not yet registered in a label map.
 */
function humanizeToken(token: string): string {
  const cleaned = token.replace(/[_-]+/g, " ").trim();
  if (!cleaned) return "Element";
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

export function elementTypeLabel(
  type: CandidateRecord["element_type"],
): string {
  // ``?? `` guards enum drift: an element_type not yet in the union (it
  // arrives as network data cast to the union) would otherwise render blank.
  // Humanize the raw token rather than surfacing nothing.
  return ELEMENT_TYPE_LABEL[type] ?? humanizeToken(type);
}

const DIRECTION_FRIENDLY: Record<string, string> = {
  positive: "lift install rate",
  negative: "avoid drop",
  brighter: "brighter",
  darker: "darker",
  win_adj: "winning direction",
  conv_dir: "conversion direction",
};

/**
 * Map a raw `hypothesis_direction` string to user-facing copy. Falls
 * back to the raw value when no friendly mapping is registered.
 */
export function directionLabel(direction: string): string {
  return DIRECTION_FRIENDLY[direction] ?? direction;
}

export type ConfidenceLevel =
  "minimal" | "thin" | "moderate" | "strong" | "confirmed";

const CONFIDENCE_RANK: Record<ConfidenceLevel, number> = {
  minimal: 1,
  thin: 2,
  moderate: 3,
  strong: 4,
  confirmed: 5,
};

/**
 * Heuristic 5-level confidence derived from `sort_score`. Mirrors the
 * Pipeline-card-fields contract in
 * `docs/product/experience-and-ui.md` (visual-only 5-level bar:
 * Minimal / Thin / Moderate / Strong / Confirmed). Until the upstream
 * Stage 7 emission carries a first-class `confidence_level` field, the
 * UI derives it from the sort_score the algorithm already returns.
 */
export function confidenceFromSortScore(sortScore: number): ConfidenceLevel {
  if (sortScore >= 0.85) return "confirmed";
  if (sortScore >= 0.65) return "strong";
  if (sortScore >= 0.45) return "moderate";
  if (sortScore >= 0.25) return "thin";
  return "minimal";
}

export function confidenceFilledSegments(level: ConfidenceLevel): number {
  return CONFIDENCE_RANK[level];
}
